import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/utils/api";
import { toast } from "sonner";
import { subDays, format } from "date-fns";
import {
  MagnifyingGlass,
  ArrowsClockwise,
  Warning,
  Package,
  Download,
  CaretUp,
  CaretDown,
  CaretRight,
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import DateRangePicker from "@/components/DateRangePicker";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// Sync status badge component
const SyncStatusBadge = ({ syncStatus }) => {
  if (!syncStatus) return null;
  
  if (syncStatus.status === "success") {
    return (
      <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
        Данные от: {syncStatus.last_sync_msk}
      </span>
    );
  }
  
  if (syncStatus.status === "never_synced") {
    return (
      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
        Требуется синхронизация
      </span>
    );
  }
  
  return null;
};

// Default date range - last 30 days
const getDefaultDateRange = () => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const from = subDays(today, 30);
  from.setHours(0, 0, 0, 0);
  return { from, to: today };
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatMoney = (value) => {
  if (value === undefined || value === null || value === 0) return "—";
  const formatted = new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
  return value < 0 ? `-${formatted} ₽` : `${formatted} ₽`;
};

const formatPercent = (value, total) => {
  if (!total || total === 0 || value === undefined || value === null) return "";
  const percent = (Math.abs(value) / total) * 100;
  return `${percent.toFixed(1)}%`;
};

const formatMoneyCompact = (value) => {
  if (value === undefined || value === null || value === 0) return "—";
  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format tax with ceiling (round up) - for conservative tax estimates
const formatTaxCeil = (value) => {
  if (value === undefined || value === null || value === 0) return "—";
  const rounded = Math.ceil(value);
  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rounded);
};

// Column header component with optional sort
const ColumnHeader = ({ children, className, sortable, sortKey, sortConfig, onSort }) => {
  const isActive = sortConfig?.key === sortKey;
  
  return (
    <TableHead 
      className={cn(
        "text-xs font-medium text-zinc-500 uppercase tracking-wider whitespace-nowrap px-2 py-3",
        sortable && "cursor-pointer hover:text-zinc-700 select-none",
        className
      )}
      onClick={sortable ? () => onSort(sortKey) : undefined}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortable && isActive && (
          sortConfig.direction === "asc" 
            ? <CaretUp size={12} weight="bold" className="text-blue-600" />
            : <CaretDown size={12} weight="bold" className="text-blue-600" />
        )}
      </div>
    </TableHead>
  );
};

// Value cell with money and percent
const ValueCell = ({ value, percent, isNegative, className }) => (
  <TableCell className={cn("px-2 py-2 text-right font-mono text-sm", className)}>
    <div className={cn(
      "flex flex-col items-end",
      isNegative && value !== 0 && "text-red-600",
      !isNegative && value > 0 && "text-emerald-600"
    )}>
      <span className="font-medium">{formatMoneyCompact(value)}{value !== 0 && " ₽"}</span>
      {percent && <span className="text-xs text-zinc-400">{percent}</span>}
    </div>
  </TableCell>
);

export default function Finance({ cachedData, cachedLoading, onRefetch }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [allTransactionsBySku, setAllTransactionsBySku] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [expandedProducts, setExpandedProducts] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "revenue", direction: "desc" });
  const [syncStatus, setSyncStatus] = useState(null);
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  
  // Pagination state
  const [limit, setLimit] = useState(15);
  const [offset, setOffset] = useState(0);
  
  // Stock data
  const [stocksData, setStocksData] = useState({});
  const [settings, setSettings] = useState({ low_stock_threshold: 10 });

  // === REACT QUERY CACHE INTEGRATION (добавлено для кэширования) ===
  useEffect(() => {
    if (cachedData && !cachedLoading) {
      setAllProducts(cachedData.products || []);
      setAllTransactionsBySku(cachedData.transactions_by_sku || {});
      setSyncStatus(cachedData.syncStatus);
      setSettings(cachedData.settings || { low_stock_threshold: 10 });
      // Build stocks map
      const stocksMap = {};
      (cachedData.stocks || []).forEach(item => {
        stocksMap[item.sku] = item.total_stock || 0;
      });
      setStocksData(stocksMap);
      setLoading(false);
    }
  }, [cachedData, cachedLoading]);
  // === END CACHE INTEGRATION ===

  // Fetch all data using FAST API (from MongoDB)
  const fetchData = async () => {
    setLoading(true);
    try {
      const [financeRes, stocksRes, settingsRes, statusRes] = await Promise.all([
        api.get(`/fast/finance`, {
          params: { limit: 10000, offset: 0 }
        }),
        api.get(`/fast/stocks`),
        api.get(`/settings`),
        api.get(`/sync/status`)
      ]);
      
      setSyncStatus(statusRes.data);
      
      if (financeRes.data.synced) {
        setAllProducts(financeRes.data.products || []);
        setAllTransactionsBySku(financeRes.data.transactions_by_sku || {});
      }
      
      if (stocksRes.data.synced) {
        const stocksMap = {};
        (stocksRes.data.items || []).forEach(item => {
          stocksMap[item.sku] = item.total_stock || 0;
        });
        setStocksData(stocksMap);
      }
      
      setSettings(settingsRes.data || { low_stock_threshold: 10 });
      
    } catch (error) {
      console.error("Error fetching finance data:", error);
      toast.error("Ошибка загрузки финансовых данных");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip if using cached data from wrapper
    if (cachedData) return;
    fetchData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Use cached refetch if available
    if (onRefetch) {
      await onRefetch();
    } else {
      await fetchData();
    }
    setRefreshing(false);
    toast.success("Данные обновлены");
  };

  // Filter and calculate data based on date range
  const { filteredProducts, totals, transactionsBySku, filteredTransactions, availableBrands, totalCount } = useMemo(() => {
    if (!allProducts.length || !dateRange?.from || !dateRange?.to) {
      return { filteredProducts: [], totals: {}, transactionsBySku: {}, filteredTransactions: {}, availableBrands: [], totalCount: 0 };
    }
    
    const fromDate = format(dateRange.from, "yyyy-MM-dd");
    const toDate = format(dateRange.to, "yyyy-MM-dd");
    
    // Filter transactions by date
    const filtered = {};
    Object.entries(allTransactionsBySku).forEach(([sku, transactions]) => {
      const filteredTx = transactions.filter(t => {
        const txDate = t.operation_date?.split(" ")[0] || t.operation_date?.split("T")[0];
        return txDate >= fromDate && txDate <= toDate;
      });
      if (filteredTx.length > 0) {
        filtered[sku] = filteredTx;
      }
    });
    
    // Collect all unique brands from products that have transactions
    const brandsSet = new Set();
    
    // Recalculate product stats from filtered transactions
    const productStats = {};
    
    Object.entries(filtered).forEach(([sku, transactions]) => {
      const productInfo = allProducts.find(p => String(p.sku) === String(sku)) || {};
      
      // Collect brand
      if (productInfo.brand) {
        brandsSet.add(productInfo.brand);
      }
      
      const stats = {
        sku: parseInt(sku),
        name: productInfo.name || "",
        offer_id: productInfo.offer_id || "",
        image_url: productInfo.primary_image || productInfo.image_url || "",
        brand: productInfo.brand || "",
        quantity_sold: 0,
        returns_count: 0,
        nevykup_count: 0,  // Добавлен счётчик невыкупов
        revenue: 0,
        commission: 0,
        logistics: 0,
        returns_cost: 0,
        advertising: productInfo.ad_spend || 0,  // Данные рекламы из sync_ads_summary
        acquiring: 0,
        cost_price: 0,
        customer_revenue: 0,
        customer_coefficient: productInfo.customer_coefficient || 0.6,
        tax_usn: 0,
        profit: 0,
        margin: 0
      };
      
      transactions.forEach(t => {
        // Новый формат: operation_type = "sale" | "return" | "nevykup"
        const txType = t.operation_type || "";
        
        if (txType === "return") {
          stats.returns_count += 1;
          stats.returns_cost += (t.logistics_return || 0) + (t.logistics_delivery || 0);
        } else if (txType === "nevykup") {
          stats.nevykup_count += 1;
          stats.returns_cost += (t.logistics_return || 0) + (t.logistics_delivery || 0);
        } else if (txType === "sale") {
          stats.quantity_sold += 1;
          stats.revenue += t.accruals_for_sale || 0;
          stats.commission += Math.abs(t.sale_commission || 0);
          stats.logistics += t.logistics_delivery || 0;
          // Сумма, оплаченная покупателем (для расчёта налога)
          stats.customer_revenue += t.customer_price || t.accruals_for_sale || 0;
          stats.acquiring += Math.abs(t.acquiring_fee || 0);
          // Версионированная себестоимость из бэкенда (по дате транзакции)
          stats.cost_price += t.cost_price || 0;
        }
      });
      
      // Calculate tax (USN 6%) from customer_revenue (already accumulated)
      stats.tax_usn = stats.customer_revenue * 0.06;
      
      // Calculate profit (себестоимость теперь приходит из транзакций)
      stats.profit = stats.revenue - stats.commission - stats.logistics - stats.returns_cost 
        - stats.advertising - stats.acquiring - stats.cost_price - stats.tax_usn;
      
      // Margin
      if (stats.revenue > 0) {
        stats.margin = (stats.profit / stats.revenue) * 100;
      }
      
      productStats[sku] = stats;
    });
    
    let products = Object.values(productStats).sort((a, b) => b.revenue - a.revenue);
    
    // Apply brand filter
    if (brandFilter && brandFilter !== "all") {
      products = products.filter(p => p.brand === brandFilter);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      products = products.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        String(p.sku).includes(query)
      );
    }
    
    // Apply sort
    if (sortConfig.key) {
      products.sort((a, b) => {
        const aVal = a[sortConfig.key] || 0;
        const bVal = b[sortConfig.key] || 0;
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      });
    }
    
    // Calculate totals (from filtered products only)
    const totals = products.reduce((acc, p) => ({
      revenue: acc.revenue + p.revenue,
      commission: acc.commission + p.commission,
      logistics: acc.logistics + p.logistics,
      returns_cost: acc.returns_cost + p.returns_cost,
      advertising: acc.advertising + p.advertising,
      acquiring: acc.acquiring + p.acquiring,
      cost_price: acc.cost_price + p.cost_price,
      tax_usn: acc.tax_usn + p.tax_usn,
      profit: acc.profit + p.profit,
      orders_count: acc.orders_count + p.quantity_sold,
      returns_count: acc.returns_count + p.returns_count
    }), {
      revenue: 0, commission: 0, logistics: 0, returns_cost: 0,
      advertising: 0, acquiring: 0, cost_price: 0, tax_usn: 0,
      profit: 0, orders_count: 0, returns_count: 0
    });
    
    if (totals.revenue > 0) {
      totals.margin = (totals.profit / totals.revenue) * 100;
    } else {
      totals.margin = 0;
    }
    
    // Save total count before pagination
    const totalCount = products.length;
    
    // Apply pagination (offset and limit)
    const paginatedProducts = products.slice(offset, offset + limit);
    
    // Sort brands alphabetically
    const sortedBrands = Array.from(brandsSet).sort();
    
    return { 
      filteredProducts: paginatedProducts, 
      totals, 
      transactionsBySku: filtered,
      filteredTransactions: filtered,
      availableBrands: sortedBrands,
      totalCount
    };
  }, [allProducts, allTransactionsBySku, dateRange, searchQuery, brandFilter, sortConfig, limit, offset]);

  // Pagination calculations
  const totalPages = Math.ceil((totalCount || 0) / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const handleSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
    setOffset(0); // Reset pagination on sort change
  };

  const toggleExpand = (sku) => {
    setExpandedProducts(prev => ({
      ...prev,
      [sku]: !prev[sku]
    }));
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ["Артикул", "Товар", "Кол-во", "Выручка", "Комиссия", "Логистика", "Реклама", "Эквайринг", "Себестоимость", "Прибыль", "Маржа"];
    const rows = filteredProducts.map(p => [
      p.sku,
      `"${p.name}"`,
      p.quantity_sold,
      p.revenue,
      p.commission,
      p.logistics,
      p.advertising,
      p.acquiring,
      p.cost_price,
      p.profit,
      `${p.margin}%`
    ]);
    
    const csv = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const today = new Date().toISOString().split('T')[0];
    a.download = `finance_report_${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Отчёт экспортирован");
  };

  if (loading && allProducts.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-chivo text-2xl font-bold text-zinc-900">Финансы</h1>
            <p className="text-sm text-zinc-500">
              Детализация по товарам и заказам
            </p>
          </div>
          <SyncStatusBadge syncStatus={syncStatus} />
        </div>
        
        <div className="flex items-center gap-2">
          {/* Date Range Picker */}
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            className="bg-white border-zinc-200 text-zinc-900 hover:bg-zinc-50 hover:text-zinc-900"
          />
          
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-finance-btn"
          >
            <ArrowsClockwise size={18} className={refreshing ? "animate-spin" : ""} />
          </Button>
          
          <Button variant="outline" className="gap-2" onClick={handleExport}>
            <Download size={16} />
            CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <CardContent className="p-3">
            <div className="text-xs text-blue-600 font-medium mb-1">Выручка</div>
            <div className="text-lg font-bold text-blue-700">{formatMoney(totals.revenue)}</div>
            <div className="text-xs text-zinc-500">{totals.orders_count || 0} заказов</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
          <CardContent className="p-3">
            <div className="text-xs text-red-600 font-medium mb-1">Комиссия</div>
            <div className="text-lg font-bold text-red-700">{formatMoney(totals.commission)}</div>
            <div className="text-xs text-zinc-500">{formatPercent(totals.commission, totals.revenue)}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
          <CardContent className="p-3">
            <div className="text-xs text-amber-600 font-medium mb-1">Логистика</div>
            <div className="text-lg font-bold text-amber-700">{formatMoney(totals.logistics)}</div>
            <div className="text-xs text-zinc-500">{formatPercent(totals.logistics, totals.revenue)}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="p-3">
            <div className="text-xs text-purple-600 font-medium mb-1">Реклама</div>
            <div className="text-lg font-bold text-purple-700">{formatMoney(totals.advertising)}</div>
            <div className="text-xs text-zinc-500">{formatPercent(totals.advertising, totals.revenue)}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-cyan-50 to-white border-cyan-100">
          <CardContent className="p-3">
            <div className="text-xs text-cyan-600 font-medium mb-1">Эквайринг</div>
            <div className="text-lg font-bold text-cyan-700">{formatMoney(totals.acquiring)}</div>
            <div className="text-xs text-zinc-500">{formatPercent(totals.acquiring, totals.revenue)}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-zinc-50 to-white border-zinc-200">
          <CardContent className="p-3">
            <div className="text-xs text-zinc-600 font-medium mb-1">Себестоимость</div>
            <div className="text-lg font-bold text-zinc-700">{formatMoney(totals.cost_price)}</div>
            <div className="text-xs text-zinc-500">{formatPercent(totals.cost_price, totals.revenue)}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-100">
          <CardContent className="p-3">
            <div className="text-xs text-orange-600 font-medium mb-1">Налог УСН 6%</div>
            <div className="text-lg font-bold text-orange-700">{formatMoney(totals.tax_usn || 0)}</div>
            <div className="text-xs text-zinc-500">{formatPercent(totals.tax_usn || 0, totals.customer_revenue || totals.revenue)}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
          <CardContent className="p-3">
            <div className="text-xs text-emerald-600 font-medium mb-1">Прибыль</div>
            <div className="text-lg font-bold text-emerald-700">{formatMoney(totals.profit)}</div>
            <div className="text-xs text-zinc-500">{totals.margin?.toFixed(1) || 0}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Поиск по артикулу или названию..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setOffset(0); // Reset pagination on search
            }}
            className="pl-10"
          />
        </div>
        
        {/* Brand Filter */}
        <Select value={brandFilter} onValueChange={(v) => {
          setBrandFilter(v);
          setOffset(0); // Reset pagination on filter change
        }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Все бренды" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все бренды</SelectItem>
            {availableBrands.map((brand) => (
              <SelectItem key={brand} value={brand}>
                {brand}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Main Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                  <TableHead className="w-10 px-2"></TableHead>
                  <TableHead className="text-xs font-medium text-zinc-500 uppercase min-w-[100px] px-2">
                    <div className="flex flex-col">
                      <span>Артикул</span>
                      <span className="text-zinc-400 normal-case">/ SKU</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-xs font-medium text-zinc-500 uppercase min-w-[350px] px-2">Товар</TableHead>
                  <ColumnHeader sortable sortKey="revenue" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[100px]">
                    Выручка
                  </ColumnHeader>
                  <ColumnHeader sortable sortKey="commission" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[100px]">
                    Комиссия
                  </ColumnHeader>
                  <ColumnHeader sortable sortKey="logistics" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[100px]">
                    Логистика
                  </ColumnHeader>
                  <ColumnHeader sortable sortKey="returns_cost" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[100px]">
                    Возвраты
                  </ColumnHeader>
                  <ColumnHeader sortable sortKey="advertising" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[100px]">
                    Реклама
                  </ColumnHeader>
                  <ColumnHeader sortable sortKey="acquiring" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[100px]">
                    Эквайринг
                  </ColumnHeader>
                  <ColumnHeader sortable sortKey="cost_price" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[100px]">
                    Себест.
                  </ColumnHeader>
                  <ColumnHeader sortable sortKey="tax" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[80px]">
                    Налог
                  </ColumnHeader>
                  <ColumnHeader sortable sortKey="profit" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[110px]">
                    Прибыль
                  </ColumnHeader>
                  <ColumnHeader sortable sortKey="margin" sortConfig={sortConfig} onSort={handleSort} className="text-right min-w-[80px]">
                    Маржа
                  </ColumnHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => {
                  const isExpanded = expandedProducts[product.sku];
                  // SKU key in transactionsBySku is string, product.sku is number
                  const productTransactions = transactionsBySku[String(product.sku)] || transactionsBySku[product.sku] || [];
                  
                  const customerRevenue = product.customer_revenue || product.revenue;
                  
                  // Filter transactions - data already comes from MongoDB with proper structure
                  // New format: operation_type = "sale" | "return" | "nevykup"
                  const allTransactions = productTransactions.filter(t => {
                    const isValidType = t.operation_type === "sale" ||
                      t.operation_type === "return" ||
                      t.operation_type === "nevykup";
                    return isValidType;
                  });
                  
                  // Calculate actual totals from transactions for product row display
                  const txSummary = allTransactions.reduce((acc, t) => {
                    const txType = t.operation_type || "sale";
                    const isSale = txType === "sale";
                    const isReturn = txType === "return";
                    const isNevykup = txType === "nevykup";
                    
                    const saleAmount = isSale ? (t.accruals_for_sale || 0) : 0;
                    const commission = isSale ? Math.abs(t.sale_commission || 0) : 0;
                    const logisticsDelivery = t.logistics_delivery || 0;
                    const logisticsReturn = t.logistics_return || 0;
                    // Эквайринг только для продаж
                    const acquiring = isSale ? Math.abs(t.acquiring_fee || 0) : 0;
                    // Налог 6% от суммы оплаченной покупателем - ТОЛЬКО для продаж
                    const customerPrice = t.customer_price || saleAmount;
                    const tax = (isSale && customerPrice > 0) ? Math.ceil(customerPrice * 0.06) : 0;
                    const adSpend = t.ad_spend || 0;
                    
                    // Cost per unit
                    const costPerUnit = isSale && product.cost_price && product.quantity_sold 
                      ? product.cost_price / product.quantity_sold 
                      : 0;
                    
                    // Profit per transaction
                    const adPerUnit = isSale && product.advertising && product.quantity_sold
                      ? product.advertising / product.quantity_sold
                      : 0;
                    
                    let txProfit;
                    if (isReturn || isNevykup) {
                      txProfit = -(logisticsDelivery + logisticsReturn);
                    } else {
                      txProfit = saleAmount - commission - logisticsDelivery - logisticsReturn - adPerUnit - acquiring - costPerUnit - tax;
                    }
                    
                    return {
                      revenue: acc.revenue + saleAmount,
                      commission: acc.commission + commission,
                      logistics: acc.logistics + logisticsDelivery,
                      logisticsReturn: acc.logisticsReturn + logisticsReturn,
                      acquiring: acc.acquiring + acquiring,
                      tax: acc.tax + tax,
                      advertising: acc.advertising + adPerUnit,
                      costPrice: acc.costPrice + costPerUnit,
                      profit: acc.profit + txProfit
                    };
                  }, { revenue: 0, commission: 0, logistics: 0, logisticsReturn: 0, acquiring: 0, tax: 0, advertising: 0, costPrice: 0, profit: 0 });
                  
                  // Use calculated values from transactions
                  const productTax = txSummary.tax;
                  const productRevenue = txSummary.revenue;
                  const productCommission = txSummary.commission;
                  const productLogistics = txSummary.logistics;
                  const productLogisticsReturn = txSummary.logisticsReturn;
                  const productAcquiring = txSummary.acquiring;
                  const productAdvertising = Math.round(txSummary.advertising);
                  const productCostPrice = Math.round(txSummary.costPrice);
                  const productProfit = Math.round(txSummary.profit);
                  
                  // Calculate margin from actual profit
                  const productMargin = productRevenue > 0 ? (productProfit / productRevenue) * 100 : 0;
                  
                  return (
                    <React.Fragment key={product.sku}>
                      {/* Product Row */}
                      <TableRow
                        className={cn(
                          "cursor-pointer transition-colors",
                          isExpanded ? "bg-blue-50 hover:bg-blue-50" : "hover:bg-zinc-50"
                        )}
                        onClick={() => toggleExpand(product.sku)}
                      >
                        <TableCell className="px-2 py-3">
                          <motion.div
                            animate={{ rotate: isExpanded ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <CaretRight size={16} className="text-zinc-400" />
                          </motion.div>
                        </TableCell>
                        <TableCell className="px-2 py-3">
                          <div className="flex flex-col">
                            <span className="font-mono text-sm font-medium text-zinc-900">
                              {product.offer_id || "-"}
                            </span>
                            <span className="font-mono text-xs text-zinc-400">
                              SKU: {product.sku}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-2 py-3">
                          <div className="flex items-start gap-3">
                            {product.image_url ? (
                              <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-12 h-12 object-cover rounded border border-zinc-200 flex-shrink-0"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className={`w-12 h-12 bg-zinc-100 rounded flex items-center justify-center flex-shrink-0 ${product.image_url ? 'hidden' : ''}`}
                            >
                              <Package size={20} className="text-zinc-400" />
                            </div>
                            <div className="min-w-[200px] max-w-[300px]">
                              <div className="text-sm font-medium text-zinc-900 leading-tight line-clamp-3" title={product.name}>
                                {product.name}
                              </div>
                              <div className="text-xs text-zinc-500 mt-1">
                                {product.quantity_sold} шт
                                {(product.returns_count > 0 || product.nevykup_count > 0) && (
                                  <span className="text-red-500">
                                    {" / "}{product.returns_count + (product.nevykup_count || 0)} шт возврат
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <ValueCell 
                          value={productRevenue} 
                          percent={formatPercent(productRevenue, totals.revenue)}
                          isNegative={false}
                        />
                        <ValueCell 
                          value={-productCommission} 
                          percent={formatPercent(productCommission, productRevenue)}
                          isNegative={true}
                        />
                        <ValueCell 
                          value={-productLogistics} 
                          percent={formatPercent(productLogistics, productRevenue)}
                          isNegative={true}
                        />
                        <ValueCell 
                          value={-productLogisticsReturn} 
                          percent={formatPercent(productLogisticsReturn, productRevenue)}
                          isNegative={true}
                        />
                        <ValueCell 
                          value={-productAdvertising} 
                          percent={formatPercent(productAdvertising, productRevenue)}
                          isNegative={true}
                        />
                        <ValueCell 
                          value={-productAcquiring} 
                          percent={formatPercent(productAcquiring, productRevenue)}
                          isNegative={true}
                        />
                        <ValueCell 
                          value={-productCostPrice} 
                          percent={formatPercent(productCostPrice, productRevenue)}
                          isNegative={true}
                        />
                        <ValueCell 
                          value={-productTax} 
                          percent={formatPercent(productTax, customerRevenue)}
                          isNegative={true}
                          className="bg-amber-50"
                        />
                        <TableCell className="px-2 py-3 text-right">
                          <div className={cn(
                            "font-mono text-sm font-bold",
                            productProfit >= 0 ? "text-emerald-600" : "text-red-600"
                          )}>
                            {formatMoney(productProfit)}
                          </div>
                          <div className="text-xs text-zinc-400">
                            {formatPercent(Math.abs(productProfit), productRevenue)}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 py-3 text-right">
                          <span className={cn(
                            "inline-flex px-2 py-1 rounded text-xs font-bold",
                            productMargin >= 30 ? "bg-emerald-100 text-emerald-700" :
                            productMargin >= 15 ? "bg-amber-100 text-amber-700" :
                            productMargin >= 0 ? "bg-orange-100 text-orange-700" :
                            "bg-red-100 text-red-700"
                          )}>
                            {productMargin.toFixed(1)}%
                          </span>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Transactions */}
                      <AnimatePresence>
                        {isExpanded && (
                          <TableRow key={`${product.sku}-expanded`}>
                            <TableCell colSpan={13} className="p-0 bg-zinc-50">
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="p-4 pl-6 overflow-x-auto">
                                  {/* Sub-header - all columns from seller24 */}
                                  <div className="min-w-[1400px]">
                                    <div className="grid grid-cols-[130px_70px_50px_120px_130px_90px_80px_80px_80px_70px_70px_80px_60px_90px] gap-1 mb-2 text-[10px] font-semibold text-zinc-500 uppercase tracking-wide border-b pb-2">
                                      <span>Заказ</span>
                                      <span>Тип</span>
                                      <span className="text-center">Кол-во</span>
                                      <span>Склад отгрузки</span>
                                      <span>Регион доставки</span>
                                      <span className="text-right">Сумма</span>
                                      <span className="text-right">Комиссия</span>
                                      <span className="text-right">Лог. дост.</span>
                                      <span className="text-right">Лог. возвр.</span>
                                      <span className="text-right">Реклама</span>
                                      <span className="text-right">Экв-нг</span>
                                      <span className="text-right">Себест.</span>
                                      <span className="text-right">Налог</span>
                                      <span className="text-right">Прибыль</span>
                                    </div>
                                    
                                    {/* Transactions list - sorted by date descending */}
                                    <div className="space-y-1 max-h-[400px] overflow-y-auto">
                                      {allTransactions.length > 0 ? (
                                        [...allTransactions]
                                          .sort((a, b) => {
                                            const dateA = a.operation_date || "";
                                            const dateB = b.operation_date || "";
                                            return dateB.localeCompare(dateA);
                                          })
                                          .map((t, idx) => {
                                            // Определяем тип операции (новый формат: sale/return/nevykup)
                                            const txType = t.operation_type || "sale";
                                            const isReturn = txType === "return";
                                            const isNevykup = txType === "nevykup";
                                            const isSale = txType === "sale";
                                            
                                            // Labels and colors based on operation type and return_type
                                            // return_type from /v1/returns/list: Cancellation, FullReturn, ClientReturn, PartialReturn
                                            let typeLabel = t.operation_type_label || "продажа";
                                            let typeColor = "bg-emerald-100 text-emerald-700";
                                            
                                            // For returns/nevykup: use return_type_label from backend if available
                                            if (isReturn || isNevykup) {
                                              const returnType = t.return_type;
                                              // Priority: return_type_label (from returns API) > operation_type_label > default
                                              typeLabel = t.return_type_label || t.operation_type_label || (isReturn ? "возврат" : "невыкуп");
                                              
                                              // Color based on return type
                                              if (returnType === "Cancellation") {
                                                typeColor = "bg-amber-100 text-amber-700"; // Отмена - оранжевый
                                              } else if (returnType === "ClientReturn") {
                                                typeColor = "bg-red-100 text-red-700"; // Возврат клиента - красный
                                              } else if (returnType === "FullReturn") {
                                                typeColor = "bg-rose-100 text-rose-700"; // Невыкуп - розовый
                                              } else if (returnType === "PartialReturn") {
                                                typeColor = "bg-orange-100 text-orange-700"; // Частичный возврат - оранжевый
                                              } else if (isNevykup) {
                                                typeColor = "bg-amber-100 text-amber-700";
                                              } else {
                                                typeColor = "bg-red-100 text-red-700";
                                              }
                                            }
                                            // For sales - always show green
                                            
                                            // Данные из агрегированных транзакций
                                            // Для продаж: показываем accruals_for_sale (выручка продавца)
                                            // Для возвратов: показываем return_amount (сумма возврата покупателю)
                                            // После исправления бэкенда, для возвратов accruals_for_sale = 0
                                            let displayAmount = 0;
                                            let amountColor = "text-blue-600";
                                            let amountPrefix = "";
                                            
                                            if (isSale) {
                                              displayAmount = t.accruals_for_sale || 0;
                                              amountColor = "text-blue-600";
                                            } else if (isReturn || isNevykup) {
                                              // For returns, show the return_amount (what was refunded to customer)
                                              // This is informational - shows what customer got back
                                              displayAmount = t.return_amount || 0;
                                              amountColor = "text-zinc-500";
                                              amountPrefix = ""; // Not negative - it's just info about refund amount
                                            }
                                            
                                            const saleAmount = isSale ? (t.accruals_for_sale || 0) : 0;
                                            const returnAmount = (isReturn || isNevykup) ? (t.return_amount || 0) : 0;
                                            const commission = isSale ? Math.abs(t.sale_commission || 0) : 0;
                                            
                                            // Логистика (уже агрегирована в бэкенде)
                                            const logisticsDelivery = t.logistics_delivery || 0;
                                            const logisticsReturn = t.logistics_return || 0;
                                            
                                            // Данные из синхронизированной транзакции
                                            const warehouseName = t.warehouse_name || "—";
                                            const deliveryRegion = t.city || t.cluster_to || "—";
                                            const acquiring = Math.abs(t.acquiring_fee || 0);
                                            
                                            // Себестоимость (версионированная из бэкенда по дате транзакции)
                                            const costPerUnit = t.cost_price || 0;
                                            
                                            // Реклама (пропорционально из общей суммы)
                                            const adPerUnit = isSale && product.advertising && product.quantity_sold
                                              ? product.advertising / product.quantity_sold
                                              : 0;
                                            
                                            // Tax (USN 6%) from customer_price (сумма оплаченная покупателем)
                                            // ВАЖНО: Налог начисляется ТОЛЬКО для продаж, не для возвратов/отмен
                                            const customerPrice = t.customer_price || saleAmount;
                                            const taxCeil = (isSale && customerPrice > 0) ? Math.ceil(customerPrice * 0.06) : 0;
                                            
                                            // Для возвратов/отмен эквайринг и комиссия должны быть 0
                                            const displayCommission = isSale ? commission : 0;
                                            const displayAcquiring = isSale ? acquiring : 0;
                                            
                                            // Прибыль (use rounded tax)
                                            let transactionProfit;
                                            if (isReturn || isNevykup) {
                                              // For returns: loss = only logistics costs (if any)
                                              // Return itself doesn't generate profit/loss, only logistics might
                                              transactionProfit = -(logisticsDelivery + logisticsReturn);
                                            } else {
                                              transactionProfit = saleAmount - displayCommission - logisticsDelivery - logisticsReturn - adPerUnit - displayAcquiring - costPerUnit - taxCeil;
                                            }
                                            
                                            return (
                                              <div 
                                                key={`${t.posting_number}_${t.sku}_${idx}`}
                                                className={cn(
                                                  "grid grid-cols-[130px_70px_50px_120px_130px_90px_80px_80px_80px_70px_70px_80px_60px_90px] gap-1 py-2 px-2 text-xs rounded border hover:border-zinc-200",
                                                  isReturn ? "bg-red-50 border-red-100" :
                                                  isNevykup ? "bg-amber-50 border-amber-100" :
                                                  t.is_international_sale ? "bg-sky-50 border-sky-100" :
                                                  "bg-white border-zinc-100 hover:bg-zinc-50"
                                                )}
                                              >
                                                {/* Заказ */}
                                                <div>
                                                  {t.posting_number ? (
                                                    <a 
                                                      href={
                                                        t.delivery_schema === 'fbs'
                                                          ? `https://seller.ozon.ru/app/postings/fbs?tab=all&postingDetails=${t.posting_number}`
                                                          : `https://seller.ozon.ru/app/postings/fbo/${t.posting_number}`
                                                      }
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="font-mono text-[11px] text-blue-600 hover:text-blue-800 hover:underline truncate block"
                                                      title={`Открыть заказ ${t.posting_number} в Ozon (${t.delivery_schema?.toUpperCase() || 'FBO'})`}
                                                      onClick={(e) => e.stopPropagation()}
                                                    >
                                                      {t.posting_number}
                                                    </a>
                                                  ) : (
                                                    <div className="font-mono text-[11px] text-zinc-700">—</div>
                                                  )}
                                                  {isReturn || isNevykup ? (
                                                    // For returns/cancellations, show return number under order number
                                                    <div className="text-[10px] space-y-0.5">
                                                      {/* Номер возврата = return_id (числовой ID для ссылки на Ozon) */}
                                                      {t.return_id && (
                                                        <a 
                                                          href={`https://seller.ozon.ru/app/returns/details/common/supply/${t.return_id}`}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="text-red-600 hover:text-red-800 hover:underline font-medium font-mono block"
                                                          title={`Открыть возврат ${t.return_id} в Ozon`}
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          {t.return_id}
                                                        </a>
                                                      )}
                                                      <div className="text-red-500">
                                                        {t.return_type_label || typeLabel}: {t.return_date?.split(" ")[0] || t.operation_date?.split(" ")[0]}
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <div className="text-[10px] text-zinc-400">
                                                      {t.operation_date?.split(" ")[0]}
                                                    </div>
                                                  )}
                                                </div>
                                                
                                                {/* Тип */}
                                                <div>
                                                  <span className={cn(
                                                    "inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium",
                                                    typeColor
                                                  )}>
                                                    {typeLabel}
                                                  </span>
                                                </div>
                                                
                                                {/* Кол-во */}
                                                <div className="text-center font-mono text-zinc-600">1</div>
                                                
                                                {/* Склад отгрузки */}
                                                <div className="text-zinc-600 truncate" title={warehouseName}>
                                                  {warehouseName}
                                                </div>
                                                
                                                {/* Регион доставки */}
                                                <div className="text-zinc-600 truncate flex items-center gap-1" title={t.is_international_sale ? `${deliveryRegion} (международный заказ, ${t.customer_currency || ''})` : deliveryRegion}>
                                                  {t.is_international_sale && (() => {
                                                    // Get country flag based on cluster_to
                                                    const cluster = (t.cluster_to || "").toLowerCase();
                                                    const flags = {
                                                      "беларусь": "🇧🇾",
                                                      "казахстан": "🇰🇿",
                                                      "узбекистан": "🇺🇿",
                                                      "киргизия": "🇰🇬",
                                                      "кыргызстан": "🇰🇬",
                                                      "армения": "🇦🇲",
                                                      "азербайджан": "🇦🇿",
                                                      "таджикистан": "🇹🇯",
                                                      "туркменистан": "🇹🇲",
                                                      "молдова": "🇲🇩",
                                                      "грузия": "🇬🇪",
                                                    };
                                                    const flag = Object.entries(flags).find(([key]) => cluster.includes(key))?.[1] || "🌍";
                                                    return <span>{flag}</span>;
                                                  })()}
                                                  {deliveryRegion}
                                                </div>
                                                
                                                {/* Сумма - выручка для продаж, сумма возврата для возвратов */}
                                                <div className={cn(
                                                  "text-right font-mono font-medium",
                                                  amountColor
                                                )}>
                                                  {isReturn || isNevykup
                                                    ? (returnAmount > 0 ? `(${formatMoneyCompact(returnAmount)})` : "—")
                                                    : formatMoneyCompact(displayAmount)
                                                  }
                                                </div>
                                                
                                                {/* Комиссия - только для продаж */}
                                                <div className="text-right font-mono text-red-500">
                                                  {isSale && commission > 0 ? (
                                                    <>-{formatMoneyCompact(commission)}</>
                                                  ) : "—"}
                                                </div>
                                                
                                                {/* Логистика доставки */}
                                                <div className="text-right font-mono text-amber-600">
                                                  {logisticsDelivery > 0 ? `-${formatMoneyCompact(logisticsDelivery)}` : "—"}
                                                </div>
                                                
                                                {/* Логистика возвратов */}
                                                <div className="text-right font-mono text-orange-500">
                                                  {logisticsReturn > 0 ? `-${formatMoneyCompact(logisticsReturn)}` : "—"}
                                                </div>
                                                
                                                {/* Реклама */}
                                                <div className="text-right font-mono text-purple-500">
                                                  {adPerUnit > 0 ? `-${formatMoneyCompact(adPerUnit)}` : "—"}
                                                </div>
                                                
                                                {/* Эквайринг */}
                                                <div className="text-right font-mono text-cyan-600">
                                                  {displayAcquiring > 0 ? `-${formatMoneyCompact(displayAcquiring)}` : "—"}
                                                </div>
                                                
                                                {/* Себестоимость */}
                                                <div className="text-right font-mono text-zinc-500">
                                                  {costPerUnit > 0 ? `-${formatMoneyCompact(costPerUnit)}` : "—"}
                                                </div>
                                                
                                                {/* Налог - только для продаж */}
                                                <div className="text-right font-mono text-zinc-400">
                                                  {taxCeil > 0 ? `-${formatTaxCeil(taxCeil)}` : "—"}
                                                </div>
                                                
                                                {/* Маржинальная прибыль */}
                                                <div className={cn(
                                                  "text-right font-mono font-bold",
                                                  transactionProfit >= 0 ? "text-emerald-600" : "text-red-600"
                                                )}>
                                                  {formatMoneyCompact(transactionProfit)}
                                                </div>
                                              </div>
                                            );
                                          })
                                      ) : (
                                        <div className="text-center py-8 text-zinc-400">
                                          Нет транзакций за выбранный период
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Summary row */}
                                    {allTransactions.length > 0 && (() => {
                                      // Calculate totals from actual transactions
                                      const txTotals = allTransactions.reduce((acc, t) => {
                                        const txType = t.operation_type || "sale";
                                        const isSale = txType === "sale";
                                        const isReturn = txType === "return";
                                        const isNevykup = txType === "nevykup";
                                        
                                        const saleAmount = isSale ? (t.accruals_for_sale || 0) : 0;
                                        const commission = isSale ? Math.abs(t.sale_commission || 0) : 0;
                                        const logisticsDelivery = t.logistics_delivery || 0;
                                        const logisticsReturn = t.logistics_return || 0;
                                        // Эквайринг только для продаж
                                        const acquiring = isSale ? Math.abs(t.acquiring_fee || 0) : 0;
                                        // Налог 6% от суммы оплаченной покупателем - ТОЛЬКО для продаж
                                        const customerPrice = t.customer_price || saleAmount;
                                        const tax = (isSale && customerPrice > 0) ? Math.ceil(customerPrice * 0.06) : 0;
                                        
                                        // Cost per unit
                                        const costPerUnit = isSale && product.cost_price && product.quantity_sold 
                                          ? product.cost_price / product.quantity_sold 
                                          : 0;
                                        
                                        // Ad per unit
                                        const adPerUnit = isSale && product.advertising && product.quantity_sold
                                          ? product.advertising / product.quantity_sold
                                          : 0;
                                        
                                        // Profit per transaction
                                        let txProfit;
                                        if (isReturn || isNevykup) {
                                          txProfit = -(logisticsDelivery + logisticsReturn);
                                        } else {
                                          txProfit = saleAmount - commission - logisticsDelivery - logisticsReturn - adPerUnit - acquiring - costPerUnit - tax;
                                        }
                                        
                                        return {
                                          count: acc.count + 1,
                                          revenue: acc.revenue + saleAmount,
                                          commission: acc.commission + commission,
                                          logisticsDelivery: acc.logisticsDelivery + logisticsDelivery,
                                          logisticsReturn: acc.logisticsReturn + logisticsReturn,
                                          advertising: acc.advertising + adPerUnit,
                                          acquiring: acc.acquiring + acquiring,
                                          costPrice: acc.costPrice + costPerUnit,
                                          tax: acc.tax + tax,
                                          profit: acc.profit + txProfit
                                        };
                                      }, { count: 0, revenue: 0, commission: 0, logisticsDelivery: 0, logisticsReturn: 0, advertising: 0, acquiring: 0, costPrice: 0, tax: 0, profit: 0 });
                                      
                                      return (
                                        <div className="grid grid-cols-[130px_70px_50px_120px_130px_90px_80px_80px_80px_70px_70px_80px_60px_90px] gap-1 py-2 px-2 mt-2 text-xs bg-zinc-200 rounded font-semibold">
                                          <div className="text-zinc-700">Итого</div>
                                          <div></div>
                                          <div className="text-center font-mono">{txTotals.count}</div>
                                          <div></div>
                                          <div></div>
                                          <div className="text-right font-mono text-blue-700">{formatMoneyCompact(txTotals.revenue)}</div>
                                          <div className="text-right font-mono text-red-600">{txTotals.commission ? `-${formatMoneyCompact(txTotals.commission)}` : "—"}</div>
                                          <div className="text-right font-mono text-amber-700">{txTotals.logisticsDelivery ? `-${formatMoneyCompact(txTotals.logisticsDelivery)}` : "—"}</div>
                                          <div className="text-right font-mono text-orange-600">{txTotals.logisticsReturn ? `-${formatMoneyCompact(txTotals.logisticsReturn)}` : "—"}</div>
                                          <div className="text-right font-mono text-purple-600">{txTotals.advertising ? `-${formatMoneyCompact(Math.round(txTotals.advertising))}` : "—"}</div>
                                          <div className="text-right font-mono text-cyan-700">{txTotals.acquiring ? `-${formatMoneyCompact(txTotals.acquiring)}` : "—"}</div>
                                          <div className="text-right font-mono text-zinc-600">{txTotals.costPrice ? `-${formatMoneyCompact(Math.round(txTotals.costPrice))}` : "—"}</div>
                                          <div className="text-right font-mono text-zinc-500">{txTotals.tax ? `-${formatMoneyCompact(txTotals.tax)}` : "—"}</div>
                                          <div className={cn(
                                            "text-right font-mono font-bold",
                                            txTotals.profit >= 0 ? "text-emerald-700" : "text-red-700"
                                          )}>
                                            {formatMoneyCompact(Math.round(txTotals.profit))}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </motion.div>
                            </TableCell>
                          </TableRow>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          
          {filteredProducts.length === 0 && !loading && (
            <div className="text-center py-16">
              <Package size={48} className="mx-auto text-zinc-300 mb-4" />
              <p className="text-lg font-medium text-zinc-600">Товары не найдены</p>
              <p className="text-sm text-zinc-400">Измените параметры поиска или период</p>
            </div>
          )}
          
          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-zinc-200">
            <div className="text-sm text-zinc-600">
              Показано <span className="font-medium">{Math.min(offset + limit, totalCount || 0)}</span> из <span className="font-medium">{totalCount || 0}</span> товаров
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-zinc-600">Отображать:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setOffset(0);
                  }}
                  className="px-3 py-1.5 text-sm border border-zinc-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={15}>15 товаров</option>
                  <option value={30}>30 товаров</option>
                  <option value={60}>60 товаров</option>
                  <option value={100}>100 товаров</option>
                </select>
              </div>
              
              {(totalCount || 0) > limit && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                  >
                    Назад
                  </Button>
                  <span className="flex items-center px-3 text-sm text-zinc-500">
                    {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= (totalCount || 0)}
                  >
                    Вперед
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
